import { DvToolbarAction, DvToolbarActionEvent } from '@devinco/ui/grid';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { FilterModelWithKeys } from '@features/vt-grid/interfaces/grid-context';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import { Entity, NEW_ENTITY_ROUTE_PARAM } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { TranslateService } from '@ngx-translate/core';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  IRowNode,
  RowNode,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { BehaviorSubject, take } from 'rxjs';
import { UserRoleDto } from '@pages/users-page/components/user-access-control-tab/interfaces';
import { AddUserRoleDialogComponent } from './add-user-role-dialog/add-user-role-dialog.component';
import { UserRoleGridService } from './user-roles-grid.service';
import { DialogService } from '@core/services/dialog.service';
import { ToolbarActionsService } from '@features/vt-grid/services/toolbar-actions.service';
import { RoleDto } from '@pages/settings-page/access-control-settings/roles/grid/interfaces';

@Component({
  selector: 'app-user-roles-grid',
  templateUrl: './user-roles-grid.component.html',
  styleUrls: ['./user-roles-grid.component.scss'],
  providers: [GridOptionsService, UserRoleGridService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UserRolesGridComponent implements OnInit {
  private translate = inject(TranslateService);
  readonly userRoleGridService = inject(UserRoleGridService);
  gridOptionService = inject(GridOptionsService);
  private dialog = inject(DialogService);
  private readonly restApi = inject(RestApiService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private toolbarActionsServie = inject(ToolbarActionsService);

  @Input() entityChanged$: BehaviorSubject<string>;
  @Output() hasRolesUpdated = new EventEmitter<boolean>();

  colDefs: ColDef[];
  gridOptions: GridOptions<UserRoleDto> | undefined;
  actions = signal<DvToolbarAction<UserRoleDto>[]>([]);
  entityId: string;

  private canEdit: boolean;
  private gridApi: GridApi | undefined;
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.actions.set(
      this.userRoleGridService.getToolbarActions(
        this.onAddClick,
        this.onDeleteClick
      )
    );
    this.setupEvents();
    this.permissionsService
      .hasPermissions([RolesAndPermissions.UserEdit])
      .pipe(take(1))
      .subscribe(hasPermissions => (this.canEdit = hasPermissions));
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
  }

  onSelectionChanged({ api }: SelectionChangedEvent): void {
    const selectedNode = api.getSelectedNodes()[0] as IRowNode | undefined;
    const gridNodes: IRowNode[] = [];

    api.forEachNode(node => {
      gridNodes.push(node);
    });

    this.actions.update(actions =>
      actions.map(action => {
        if (action.icon === 'delete') {
          return {
            ...action,
            disabled: !this.canEdit || !selectedNode || gridNodes.length === 1,
          };
        }
        if (action.icon === 'add_box') {
          return { ...action, disabled: !this.canEdit };
        }
        return action;
      })
    );
  }

  private setupEvents(): void {
    this.entityChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(entityId => {
        this.entityId = entityId === NEW_ENTITY_ROUTE_PARAM ? '0' : entityId;

        if (!this.gridOptions) {
          this.setupVariables();
        }

        this.gridOptions!.context.extraFilter = this.getFilterModel(
          this.entityId
        );
        this.gridOptions?.context.loadClientSideData?.(
          this.getFilterModel(this.entityId),
          true
        );
        this.gridOptions!.rowGroupPanelShow = 'never';
      });
  }

  private onAddClick = (): void => {
    this.openModal();
  };

  private onDeleteClick = (event: DvToolbarActionEvent): void => {
    this.deleteRoles(event);
  };

  private setupVariables(): void {
    this.gridOptions = this.gridOptionService.clientSide(
      this.userRoleGridService.colDefs,
      Entity.UserRole,
      this.getFilterModel(this.entityId)
    );
  }

  private openModal(): void {
    this.dialog
      .open<UserRoleDto[], AddUserRoleDialogComponent>({
        size: 'lg',
        title: 'User.Roles.SelectRole',
        confirmText: 'General.Save',
        cancelText: 'General.Cancel',
        contentComponent: AddUserRoleDialogComponent,
        hideFooter: true,
        data: this.getExistingRoles(),
      })
      .closed.pipe(take(1))
      .subscribe(({ responseData, source }) => {
        if (!responseData || source !== 'confirm') return;
        this.addRolesToGrid(responseData);
      });
  }

  private addRolesToGrid(newNodes: RowNode<RoleDto>[]): void {
    if (this.gridOptions) {
      const newRoles = newNodes.map(node => {
        return { ...node.data, RoleDescription: node.data?.Description };
      });
      const newRoleIDs = newRoles.map(role => role.RoleID) as number[];

      this.restApi
        .upsert(Entity.User, this.entityId, {
          Roles: [
            ...newRoleIDs,
            ...this.getExistingRoles().map(role => role.RoleID),
          ],
        })
        .subscribe(() => {
          this.gridOptions?.context.loadClientSideData?.(
            this.getFilterModel(this.entityId)
          );
          this.hasRolesUpdated.emit(true);
        });
    }
  }

  private deleteRoles(event: DvToolbarActionEvent<UserRoleDto>): void {
    this.toolbarActionsServie
      .openRowDeleteDialog()
      .closed.pipe(take(1))
      .subscribe(({ source }) => {
        if (source === 'confirm') {
          const selectedRowData: UserRoleDto =
            event.api.getSelectedNodes()[0].data!;
          this.restApi
            .delete(Entity.UserRole, selectedRowData.UserRoleID)
            .subscribe(() => {
              event.api.applyTransaction({ remove: [selectedRowData!] });
              this.hasRolesUpdated.emit(true);
            });
        }
      });
  }

  private getExistingRoles(): UserRoleDto[] {
    const rowData: UserRoleDto[] | undefined = [];
    this.gridApi?.forEachNode(node => rowData.push(node.data));
    return rowData;
  }

  private getFilterModel(entityId: string): FilterModelWithKeys {
    return {
      UserID: {
        filterType: 'number',
        type: 'equals',
        filter: entityId,
      },
    };
  }
}
